import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { api, errDetail, fmtIST } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Skeleton } from "@/components/ui/skeleton";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Play, Trash2, Pencil, Zap, Clock } from "lucide-react";

const OPS = [["eq", "equals"], ["ne", "not equals"], ["contains", "contains"], ["exists", "exists"]];
const TIMING_MODES = [
  ["immediate", "Immediately"],
  ["delay", "Delay after trigger"],
  ["at", "Exact date/time"],
  ["before_event", "Before the event time"],
  ["after_event", "After the event time"],
];
const UNITS = [["minutes", "minutes"], ["hours", "hours"], ["days", "days"]];
const VAR_SOURCES = [
  ["contact.first_name", "Customer first name"],
  ["contact.full_name", "Customer full name"],
  ["contact.customer_id", "Customer ID"],
  ["ctx.event_time", "Event date/time"],
  ["ctx.product_code", "Product"],
  ["ctx.stage_name", "Stage"],
  ["static", "Static value…"],
];

const emptyAction = () => ({ type: "create_task", params: { title: "Follow up", due_days: 1, priority: "high" } });
const emptyForm = () => ({
  name: "", description: "", trigger: "lead_created", conditions: [],
  timing: { mode: "immediate", delay_value: 0, delay_unit: "minutes", at: "", business_hours: false },
  actions: [emptyAction()], enabled: false, test_mode: true,
});

const toApiTiming = (t) => {
  const out = { mode: t.mode, business_hours: !!t.business_hours };
  if (["delay", "before_event", "after_event"].includes(t.mode)) {
    const v = Number(t.delay_value || 0);
    out.delay_minutes = t.delay_unit === "minutes" ? v : 0;
    out.delay_hours = t.delay_unit === "hours" ? v : 0;
    out.delay_days = t.delay_unit === "days" ? v : 0;
  }
  if (t.mode === "at" && t.at) out.at = t.at;
  return out;
};
const fromApiTiming = (t) => {
  if (!t) return emptyForm().timing;
  let delay_value = 0, delay_unit = "minutes";
  if (Number(t.delay_days || 0) > 0) { delay_value = Number(t.delay_days); delay_unit = "days"; }
  else if (Number(t.delay_hours || 0) > 0) { delay_value = Number(t.delay_hours); delay_unit = "hours"; }
  else if (Number(t.delay_minutes || 0) > 0) { delay_value = Number(t.delay_minutes); delay_unit = "minutes"; }
  return { mode: t.mode || "immediate", delay_value, delay_unit, at: t.at || "", business_hours: !!t.business_hours };
};
const timingLabel = (t) => {
  if (!t || t.mode === "immediate" || !t.mode) return "immediately";
  if (t.mode === "at") return `at ${t.at}`;
  const parts = [];
  if (Number(t.delay_minutes || 0)) parts.push(`${t.delay_minutes} min`);
  if (Number(t.delay_hours || 0)) parts.push(`${t.delay_hours} hr`);
  if (Number(t.delay_days || 0)) parts.push(`${t.delay_days} day(s)`);
  const off = parts.join(" ") || "0 min";
  const bh = t.business_hours ? " (business hours)" : "";
  if (t.mode === "before_event") return `${off} before event${bh}`;
  if (t.mode === "after_event") return `${off} after event${bh}`;
  return `after ${off}${bh}`;
};

/* Per-action parameter editor — every provider action is configuration. */
const ActionEditor = ({ action, onChange, onRemove, index, webhooks, users, waTemplates, roles }) => {
  const p = action.params || {};
  const set = (k, v) => onChange({ ...action, params: { ...p, [k]: v } });
  const setVars = (vars) => set("variables", vars);
  const waTpl = waTemplates?.[p.kind];
  return (
    <div className="rounded-md border p-2.5 space-y-2" data-testid={`action-editor-${index}`}>
      <div className="flex items-center gap-2">
        <ActionTypeSelect value={action.type} onChange={(t) => onChange({ type: t, params: {} })} index={index} />
        <Button size="sm" variant="ghost" className="h-7 px-1.5 ml-auto" data-testid={`action-remove-${index}`} onClick={onRemove}><Trash2 className="h-3.5 w-3.5" /></Button>
      </div>
      {["create_task", "schedule_followup"].includes(action.type) && (
        <div className="grid grid-cols-2 gap-2">
          <Input data-testid={`action-${index}-title`} placeholder="Task title" className="h-8 text-xs" value={p.title || ""} onChange={(e) => set("title", e.target.value)} />
          <Input data-testid={`action-${index}-due`} type="number" placeholder="Due in days" className="h-8 text-xs" value={p.due_days ?? 1} onChange={(e) => set("due_days", Number(e.target.value))} />
          <Select value={p.priority || "high"} onValueChange={(v) => set("priority", v)}>
            <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
            <SelectContent>{["low", "medium", "high", "urgent"].map((x) => <SelectItem key={x} value={x}>{x}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={p.owner_user_id || "record_owner"} onValueChange={(v) => set("owner_user_id", v === "record_owner" ? null : v)}>
            <SelectTrigger className="h-8 text-xs"><SelectValue placeholder="Owner" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="record_owner">Record owner</SelectItem>
              {users.map((u) => <SelectItem key={u.user_id} value={u.user_id}>{u.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      )}
      {action.type === "send_whatsapp_template" && (
        <div className="space-y-2">
          <Select value={p.kind || ""} onValueChange={(v) => set("kind", v)}>
            <SelectTrigger data-testid={`action-${index}-template`} className="h-8 text-xs"><SelectValue placeholder="Choose approved template…" /></SelectTrigger>
            <SelectContent>
              {Object.entries(waTemplates || {}).map(([k, t]) => <SelectItem key={k} value={k}>{t.purpose || k}</SelectItem>)}
            </SelectContent>
          </Select>
          {p.kind && (
            <p className="text-[11px] text-muted-foreground">
              Interakt template: <span className="font-mono">{waTpl?.name || "not mapped yet"}</span> ({waTpl?.language || "en"})
              {!waTpl?.name && " — map it in Admin → Integrations → WhatsApp before enabling this automation"}
            </p>
          )}
          <Label className="text-[11px] text-muted-foreground">Variable mapping ({"{{1}}, {{2}}"} … in order — never hard-coded)</Label>
          {(p.variables || []).map((v, vi) => (
            <div key={vi} className="flex items-center gap-2">
              <span className="text-[11px] font-mono w-10">{`{{${vi + 1}}}`}</span>
              <Select value={v.source} onValueChange={(s) => { const nv = [...p.variables]; nv[vi] = { ...nv[vi], source: s }; setVars(nv); }}>
                <SelectTrigger data-testid={`action-${index}-var-${vi}`} className="h-8 text-xs flex-1"><SelectValue /></SelectTrigger>
                <SelectContent>{VAR_SOURCES.map(([s, l]) => <SelectItem key={s} value={s}>{l}</SelectItem>)}</SelectContent>
              </Select>
              {v.source === "static" && <Input className="h-8 text-xs flex-1" placeholder="Static value" value={v.value || ""} onChange={(e) => { const nv = [...p.variables]; nv[vi] = { ...nv[vi], value: e.target.value }; setVars(nv); }} />}
              <Button size="sm" variant="ghost" className="h-7 px-1.5" onClick={() => setVars(p.variables.filter((_, x) => x !== vi))}><Trash2 className="h-3 w-3" /></Button>
            </div>
          ))}
          <Button size="sm" variant="outline" className="h-7 text-xs" data-testid={`action-${index}-add-var`} onClick={() => setVars([...(p.variables || []), { source: "contact.first_name" }])}><Plus className="h-3 w-3 mr-1" />Add variable</Button>
        </div>
      )}
      {action.type === "trigger_voice" && (
        <div className="grid grid-cols-3 gap-2">
          <Input data-testid={`action-${index}-agent`} placeholder="ElevenLabs agent ID (optional)" className="h-8 text-xs col-span-2" value={p.agent_id || ""} onChange={(e) => set("agent_id", e.target.value)} />
          <Input data-testid={`action-${index}-voicedelay`} type="number" placeholder="Dial delay min" className="h-8 text-xs" value={p.delay_minutes ?? 2} onChange={(e) => set("delay_minutes", Number(e.target.value))} />
          <p className="text-[10px] text-muted-foreground col-span-3">Booked consultations cancel pending calls automatically (stale-check). Attempt caps and calling window come from Ops Policies unless overridden.</p>
        </div>
      )}
      {action.type === "send_email" && (
        <Input data-testid={`action-${index}-email-template`} placeholder="Email template key" className="h-8 text-xs" value={p.template || ""} onChange={(e) => set("template", e.target.value)} />
      )}
      {["add_tag", "remove_tag"].includes(action.type) && (
        <Input data-testid={`action-${index}-tag`} placeholder="Tag name" className="h-8 text-xs" value={p.tag || ""} onChange={(e) => set("tag", e.target.value)} />
      )}
      {action.type === "change_stage" && (
        <Input data-testid={`action-${index}-stage`} placeholder="Target stage name" className="h-8 text-xs" value={p.stage_name || ""} onChange={(e) => set("stage_name", e.target.value)} />
      )}
      {action.type === "notify_role" && (
        <div className="grid grid-cols-3 gap-2">
          <Select value={p.role || "manager"} onValueChange={(v) => set("role", v)}>
            <SelectTrigger data-testid={`action-${index}-role`} className="h-8 text-xs"><SelectValue placeholder="Role" /></SelectTrigger>
            <SelectContent>{(roles || []).map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
          </Select>
          <Input placeholder="Title" className="h-8 text-xs col-span-2" value={p.title || ""} onChange={(e) => set("title", e.target.value)} />
        </div>
      )}
      {action.type === "send_meta_conversion" && (
        <div className="grid grid-cols-2 gap-2">
          <Input data-testid={`action-${index}-meta-dest`} placeholder="destination: product OR dataset key" className="h-8 text-xs" value={p.destination || "product"} onChange={(e) => set("destination", e.target.value)} />
          <Input data-testid={`action-${index}-meta-event`} placeholder="Event name (Lead)" className="h-8 text-xs" value={p.event_name || "Lead"} onChange={(e) => set("event_name", e.target.value)} />
          <p className="text-[10px] text-muted-foreground col-span-2">'product' uses the Dataset Manager product mapping — a missing mapping SKIPS with a log, never routes elsewhere.</p>
        </div>
      )}
      {action.type === "outbound_webhook" && (
        <Select value={p.webhook_id || ""} onValueChange={(v) => set("webhook_id", v)}>
          <SelectTrigger data-testid={`action-${index}-webhook`} className="h-8 text-xs"><SelectValue placeholder="Choose outbound webhook" /></SelectTrigger>
          <SelectContent>{webhooks.map((w) => <SelectItem key={w.webhook_id} value={w.webhook_id}>{w.name || w.url}</SelectItem>)}</SelectContent>
        </Select>
      )}
      {action.type === "create_payment_reminder" && (
        <div className="grid grid-cols-2 gap-2">
          <Input placeholder="Task title (optional)" className="h-8 text-xs" value={p.title || ""} onChange={(e) => set("title", e.target.value)} />
          <Input type="number" placeholder="Due days" className="h-8 text-xs" value={p.due_days ?? 1} onChange={(e) => set("due_days", Number(e.target.value))} />
        </div>
      )}
      {action.type === "assign_owner" && (
        <Select value={p.user_id || ""} onValueChange={(v) => set("user_id", v)}>
          <SelectTrigger className="h-8 text-xs"><SelectValue placeholder="Assign to…" /></SelectTrigger>
          <SelectContent>{users.map((u) => <SelectItem key={u.user_id} value={u.user_id}>{u.name}</SelectItem>)}</SelectContent>
        </Select>
      )}
      {action.type === "create_invoice_task" && (
        <div className="space-y-2">
          <div className="grid grid-cols-2 gap-2">
            <Input placeholder="Task title (optional)" className="h-8 text-xs" value={p.title || ""} onChange={(e) => set("title", e.target.value)} />
            <Input type="number" placeholder="Due days" className="h-8 text-xs" value={p.due_days ?? 1} onChange={(e) => set("due_days", Number(e.target.value))} />
          </div>
          <Label className="text-[11px] text-muted-foreground">Assign the task to</Label>
          <div className="grid grid-cols-2 gap-2">
            <Select value={p.mode || "owner"} onValueChange={(v) => set("mode", v)}>
              <SelectTrigger data-testid={`action-${index}-invoice-mode`} className="h-8 text-xs"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="owner">The opportunity's owner</SelectItem>
                <SelectItem value="user">A designated user</SelectItem>
                <SelectItem value="role">A role / shared queue</SelectItem>
              </SelectContent>
            </Select>
            {p.mode === "user" && (
              <Select value={p.user_id || ""} onValueChange={(v) => set("user_id", v)}>
                <SelectTrigger data-testid={`action-${index}-invoice-user`} className="h-8 text-xs"><SelectValue placeholder="User…" /></SelectTrigger>
                <SelectContent>{users.map((u) => <SelectItem key={u.user_id} value={u.user_id}>{u.name}</SelectItem>)}</SelectContent>
              </Select>
            )}
            {p.mode === "role" && (
              <Select value={p.role || ""} onValueChange={(v) => set("role", v)}>
                <SelectTrigger data-testid={`action-${index}-invoice-role`} className="h-8 text-xs"><SelectValue placeholder="Role…" /></SelectTrigger>
                <SelectContent>{(roles || []).map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
              </Select>
            )}
          </div>
        </div>
      )}
      {action.type === "create_ticket" && (
        <div className="grid grid-cols-2 gap-2">
          <Input placeholder="Subject (optional)" className="h-8 text-xs" value={p.subject || ""} onChange={(e) => set("subject", e.target.value)} />
          <Select value={p.priority || "medium"} onValueChange={(v) => set("priority", v)}>
            <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
            <SelectContent>{["low", "medium", "high", "urgent"].map((x) => <SelectItem key={x} value={x}>{x}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      )}
      {action.type === "request_document" && (
        <div className="grid grid-cols-2 gap-2">
          <Input placeholder="Document name (optional)" className="h-8 text-xs" value={p.name || ""} onChange={(e) => set("name", e.target.value)} />
          <Input placeholder="Category (optional)" className="h-8 text-xs" value={p.category || ""} onChange={(e) => set("category", e.target.value)} />
        </div>
      )}
      {action.type === "create_calendar_note" && (
        <Input placeholder="Note title (optional)" className="h-8 text-xs" value={p.title || ""} onChange={(e) => set("title", e.target.value)} />
      )}
      {action.type === "notify_user" && (
        <div className="grid grid-cols-2 gap-2">
          <Select value={p.user_id || ""} onValueChange={(v) => set("user_id", v)}>
            <SelectTrigger className="h-8 text-xs"><SelectValue placeholder="Notify…" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="">Record owner</SelectItem>
              {users.map((u) => <SelectItem key={u.user_id} value={u.user_id}>{u.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Input placeholder="Notification title (optional)" className="h-8 text-xs" value={p.title || ""} onChange={(e) => set("title", e.target.value)} />
        </div>
      )}
      {action.type === "create_approval" && (
        <Select value={p.approval_type || "exception"} onValueChange={(v) => set("approval_type", v)}>
          <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
          <SelectContent>
            {["exception", "ownership_change", "product_addition"].map((x) => <SelectItem key={x} value={x}>{x.replace(/_/g, " ")}</SelectItem>)}
          </SelectContent>
        </Select>
      )}
      {["ai_advisory", "create_consultation_invitation"].includes(action.type) && (
        <p className="text-[11px] text-muted-foreground">
          {action.type === "ai_advisory"
            ? "Generates an AI summary for the customer this event fired for — no extra parameters."
            : "Sends the configured booking-invitation WhatsApp template for this opportunity's product — no extra parameters."}
        </p>
      )}
    </div>
  );
};

let META_CACHE = { triggers: [], actions: [] };
const ActionTypeSelect = ({ value, onChange, index }) => (
  <Select value={value} onValueChange={onChange}>
    <SelectTrigger data-testid={`action-type-${index}`} className="h-8 text-xs w-64"><SelectValue /></SelectTrigger>
    <SelectContent>{META_CACHE.actions.map((a) => <SelectItem key={a} value={a}>{a.replace(/_/g, " ")}</SelectItem>)}</SelectContent>
  </Select>
);

export const AutomationsTab = () => {
  const [rules, setRules] = useState(null);
  const [meta, setMeta] = useState({ triggers: [], actions: [] });
  const [runs, setRuns] = useState([]);
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [webhooks, setWebhooks] = useState([]);
  const [users, setUsers] = useState([]);
  const [waTemplates, setWaTemplates] = useState({});
  const [roles, setRoles] = useState([]);
  const load = useCallback(() => {
    api.get("/automations").then((r) => setRules(r.data));
    api.get("/automations/runs?limit=25").then((r) => setRuns(r.data));
  }, []);
  useEffect(() => {
    load();
    api.get("/automations/meta").then((r) => { META_CACHE = r.data; setMeta(r.data); });
    api.get("/outbound-webhooks").then((r) => setWebhooks(r.data)).catch(() => {});
    api.get("/users").then((r) => setUsers((r.data.users || r.data).filter((u) => u.is_active !== false))).catch(() => {});
    api.get("/comms/whatsapp/templates").then((r) => setWaTemplates(r.data.templates || {})).catch(() => {});
    api.get("/users/roles").then((r) => setRoles(r.data.map((x) => x.role))).catch(() => {});
  }, [load]);

  const patch = async (rule, updates) => {
    try {
      await api.patch(`/automations/${rule.automation_id}`, { ...rule, ...updates });
      load();
    } catch (e) { toast.error(errDetail(e)); }
  };
  const openNew = () => { setEditingId(null); setForm(emptyForm()); setOpen(true); };
  const openEdit = (r) => {
    setEditingId(r.automation_id);
    setForm({ name: r.name, description: r.description || "", trigger: r.trigger, conditions: r.conditions || [], timing: fromApiTiming(r.timing), actions: (r.actions || []).map((a) => ({ ...a, params: a.params || {} })), enabled: r.enabled, test_mode: r.test_mode });
    setOpen(true);
  };
  const save = async () => {
    const body = { ...form, timing: toApiTiming(form.timing) };
    try {
      if (editingId) { await api.patch(`/automations/${editingId}`, body); toast.success("Automation updated"); }
      else { await api.post("/automations", body); toast.success("Automation created (disabled by default)"); }
      setOpen(false); load();
    } catch (e) { toast.error(errDetail(e)); }
  };
  const test = async (rule) => {
    try {
      const { data } = await api.post(`/automations/${rule.automation_id}/test`);
      toast.success(`Test run: ${(data.results || []).map((r) => r.result).join("; ").slice(0, 120) || "no matching run"}`);
      load();
    } catch (e) { toast.error(errDetail(e)); }
  };
  const setCond = (i, k, v) => {
    const c = [...form.conditions]; c[i] = { ...c[i], [k]: v }; setForm({ ...form, conditions: c });
  };
  const setAction = (i, a) => { const acts = [...form.actions]; acts[i] = a; setForm({ ...form, actions: acts }); };

  if (!rules) return <Skeleton className="h-48" />;
  return (
    <div className="space-y-4" data-testid="automations-admin">
      <div className="flex justify-between items-center gap-2">
        <p className="text-xs text-muted-foreground max-w-xl">Unified business automation builder — <b>WHEN</b> trigger → <b>IF</b> conditions → <b>WHEN</b> timing/delay → <b>THEN</b> actions. Delays are durable server-side jobs (restart-safe) and re-check record state before firing (stale-action prevention). Interakt, ElevenLabs, Meta, Finance and CRM actions are all available here.</p>
        <Button data-testid="automation-new-button" size="sm" onClick={openNew}><Plus className="h-4 w-4 mr-1" /> New automation</Button>
      </div>
      {rules.map((r) => (
        <Card key={r.automation_id} data-testid={`automation-${r.automation_id}`}>
          <CardContent className="pt-4 flex items-center gap-3 flex-wrap">
            <div className="flex-1 min-w-64">
              <p className="text-sm font-medium flex items-center gap-2"><Zap className="h-3.5 w-3.5 text-primary" />{r.name}{r.seeded && <Badge variant="outline" className="text-[9px]">default</Badge>}</p>
              <p className="text-xs text-muted-foreground">
                WHEN <b>{r.trigger.replace(/_/g, " ")}</b>
                {(r.conditions || []).length > 0 && <> IF {r.conditions.map((c) => `${c.field} ${c.op} ${c.value ?? ""}`).join(" & ")}</>}
                {" "}<Clock className="h-3 w-3 inline" /> {timingLabel(r.timing)}
                {" "}THEN {(r.actions || []).map((a) => a.type.replace(/_/g, " ")).join(" → ")}
              </p>
              {r.description && <p className="text-[11px] text-muted-foreground mt-0.5">{r.description}</p>}
            </div>
            {r.test_mode && <Badge variant="outline" className="text-[10px]">Test mode</Badge>}
            <Button data-testid={`automation-edit-${r.automation_id}`} size="sm" variant="outline" className="h-7 text-xs" onClick={() => openEdit(r)}><Pencil className="h-3 w-3 mr-1" /> Edit</Button>
            <Button data-testid={`automation-test-${r.automation_id}`} size="sm" variant="outline" className="h-7 text-xs" onClick={() => test(r)}><Play className="h-3 w-3 mr-1" /> Test</Button>
            <Switch data-testid={`automation-toggle-${r.automation_id}`} checked={r.enabled} onCheckedChange={(v) => patch(r, { enabled: v })} />
          </CardContent>
        </Card>
      ))}
      <Card><CardHeader className="pb-2"><CardTitle className="text-sm font-heading">Run history</CardTitle></CardHeader>
        <CardContent className="space-y-1">
          {runs.length === 0 && <p className="text-xs text-muted-foreground">No automation runs yet.</p>}
          {runs.map((r) => (
            <p key={r.run_id} className="text-xs" data-testid={`run-${r.run_id}`}>
              <span className="text-muted-foreground">{fmtIST(r.at, { year: true })}</span> · <b>{r.automation_name}</b> on {r.event}
              {r.scheduled_for && <span className="text-muted-foreground"> (scheduled {fmtIST(r.scheduled_for)})</span>}
              {" → "}{(r.results || []).map((x) => x.result).join("; ").slice(0, 120)}
            </p>
          ))}
        </CardContent>
      </Card>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[88vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading">{editingId ? "Edit automation" : "New automation"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-2">
              <Input data-testid="automation-name" placeholder="Automation name…" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
              <Input data-testid="automation-description" placeholder="Description (optional)" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
            </div>
            <div className="space-y-1"><Label className="text-xs font-semibold">WHEN — trigger</Label>
              <Select value={form.trigger} onValueChange={(v) => setForm({ ...form, trigger: v })}>
                <SelectTrigger data-testid="automation-trigger" className="h-9"><SelectValue /></SelectTrigger>
                <SelectContent className="max-h-72">{meta.triggers.map((t) => <SelectItem key={t} value={t}>{t.replace(/_/g, " ")}</SelectItem>)}</SelectContent>
              </Select></div>
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold">IF — conditions (all must match)</Label>
              {form.conditions.map((c, i) => (
                <div key={i} className="grid grid-cols-[1fr_120px_1fr_32px] gap-2">
                  <Input data-testid={`cond-field-${i}`} placeholder="field (product_code, intent, stage_name…)" className="h-8 text-xs" value={c.field || ""} onChange={(e) => setCond(i, "field", e.target.value)} />
                  <Select value={c.op || "eq"} onValueChange={(v) => setCond(i, "op", v)}>
                    <SelectTrigger data-testid={`cond-op-${i}`} className="h-8 text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent>{OPS.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
                  </Select>
                  <Input data-testid={`cond-value-${i}`} placeholder="value" className="h-8 text-xs" value={c.value ?? ""} onChange={(e) => setCond(i, "value", e.target.value)} />
                  <Button size="sm" variant="ghost" className="h-8 px-1.5" onClick={() => setForm({ ...form, conditions: form.conditions.filter((_, x) => x !== i) })}><Trash2 className="h-3.5 w-3.5" /></Button>
                </div>
              ))}
              <Button data-testid="cond-add" size="sm" variant="outline" className="h-7 text-xs" onClick={() => setForm({ ...form, conditions: [...form.conditions, { field: "", op: "eq", value: "" }] })}><Plus className="h-3 w-3 mr-1" />Add condition</Button>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold">WHEN — timing / delay</Label>
              <div className="grid grid-cols-2 gap-2">
                <Select value={form.timing.mode} onValueChange={(v) => setForm({ ...form, timing: { ...form.timing, mode: v } })}>
                  <SelectTrigger data-testid="timing-mode" className="h-9"><SelectValue /></SelectTrigger>
                  <SelectContent>{TIMING_MODES.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
                </Select>
                {["delay", "before_event", "after_event"].includes(form.timing.mode) && (
                  <div className="flex gap-2">
                    <Input data-testid="timing-value" type="number" className="h-9 w-24" value={form.timing.delay_value} onChange={(e) => setForm({ ...form, timing: { ...form.timing, delay_value: e.target.value } })} />
                    <Select value={form.timing.delay_unit} onValueChange={(v) => setForm({ ...form, timing: { ...form.timing, delay_unit: v } })}>
                      <SelectTrigger data-testid="timing-unit" className="h-9"><SelectValue /></SelectTrigger>
                      <SelectContent>{UNITS.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                )}
                {form.timing.mode === "at" && (
                  <Input data-testid="timing-at" type="datetime-local" className="h-9" value={form.timing.at} onChange={(e) => setForm({ ...form, timing: { ...form.timing, at: e.target.value } })} />
                )}
              </div>
              <label className="flex items-center gap-2 text-xs">
                <Checkbox data-testid="timing-business-hours" checked={form.timing.business_hours} onCheckedChange={(v) => setForm({ ...form, timing: { ...form.timing, business_hours: !!v } })} />
                Business-hours aware (defer to the configured working window)
              </label>
              <p className="text-[11px] text-muted-foreground">Scheduled runs are persisted server-side and survive backend restarts. State is re-checked before firing.</p>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold">THEN — actions (run in order)</Label>
              {form.actions.map((a, i) => (
                <ActionEditor key={i} action={a} index={i} webhooks={webhooks} users={users} waTemplates={waTemplates} roles={roles}
                  onChange={(na) => setAction(i, na)}
                  onRemove={() => setForm({ ...form, actions: form.actions.filter((_, x) => x !== i) })} />
              ))}
              <Button data-testid="action-add" size="sm" variant="outline" className="h-7 text-xs" onClick={() => setForm({ ...form, actions: [...form.actions, emptyAction()] })}><Plus className="h-3 w-3 mr-1" />Add action</Button>
            </div>
            <label className="flex items-center gap-2 text-xs">
              <Checkbox data-testid="automation-testmode" checked={form.test_mode} onCheckedChange={(v) => setForm({ ...form, test_mode: !!v })} /> Test mode (log only, no real actions)
            </label>
          </div>
          <DialogFooter><Button data-testid="automation-save" disabled={!form.name.trim() || form.actions.length === 0} onClick={save}>{editingId ? "Save changes" : "Create automation"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
